import { useEffect, useRef, useState } from 'react';

// A burst of same-category tool calls (see groupToolStepsByCategory) merges
// into one row whose icon tracks the MOST RECENT call's status — so a run of
// several quick calls (each resolving in well under a second) flips that icon
// check -> spinner -> check on every call boundary as the count climbs,
// reading as a flash rather than a repeat-count. Delaying only the ON
// transition means a call that finishes before DELAY_MS never shows the
// spinner at all; a genuinely slow call still shows it, and hides it the
// instant it completes (that transition is immediate, not debounced).
const DELAY_MS = 150;

export default function useDelayedRunning(
  running: boolean | undefined,
  enabled = true,
): boolean {
  const [delayedRunning, setDelayedRunning] = useState(
    enabled ? false : !!running,
  );
  const runningRef = useRef(running);
  runningRef.current = running;

  useEffect(() => {
    if (!enabled) {
      setDelayedRunning(!!running);
      return undefined;
    }
    if (!running) {
      setDelayedRunning(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (runningRef.current) setDelayedRunning(true);
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, [running, enabled]);

  return delayedRunning;
}
